import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Check,
  Link2,
  Package,
  Tags,
} from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { BulkMapFromSku } from "../../components/bulk-map-from-sku";
import { PageHeader } from "../../components/page-header";
import { trackEventOnce } from "../../lib/analytics";
import { api, unwrap } from "../../lib/api";
import { secureAssetUrl } from "../../lib/url";

type SetupSearch = { status?: string };

export const Route = createFileRoute("/_app/setup")({
  validateSearch: (search: Record<string, unknown>): SetupSearch => ({
    status: typeof search.status === "string" ? search.status : undefined,
  }),
  component: SetupPage,
});

const STEPS = [
  { id: "connect", label: "Conectar", icon: Link2 },
  { id: "import", label: "Importar", icon: Tags },
  { id: "map", label: "Mapear", icon: Package },
] as const;

function SetupPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    if (!search.status) return;
    if (search.status === "connected") {
      trackEventOnce("StoreConnected", "fc-analytics-store-connected");
      toastRef.current.add({
        type: "success",
        title: "Conta conectada",
        description: "Agora importe os anúncios desta loja.",
      });
    } else if (search.status === "account_limit") {
      toastRef.current.add({
        type: "error",
        title: "Limite de lojas do seu plano atingido",
      });
    }
    void navigate({ search: {}, replace: true });
  }, [search.status, navigate]);

  const setup = useQuery({
    queryKey: ["setup"],
    queryFn: async () => unwrap(await api.setup.get()),
    refetchInterval: (query) =>
      query.state.data?.step === "import" ||
      (query.state.data?.import.active ?? 0) > 0 ||
      (query.state.data?.import.waiting ?? 0) > 0
        ? 2000
        : false,
  });

  if (setup.isLoading || !setup.data) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const step = setup.data.step;

  return (
    <>
      <PageHeader
        title="Configuração inicial"
        description="Conecte a loja, importe o catálogo e vincule os SKUs para o estoque central valer nas vendas."
      />

      <ol className="mb-5 flex w-full items-center">
        {STEPS.map((item, index) => {
          const reached =
            STEPS.findIndex((s) => s.id === step) >= index || step === "done";
          const current = step === item.id;
          return (
            <Fragment key={item.id}>
              <li className="flex shrink-0 items-center gap-2">
                <div
                  className={
                    current
                      ? "flex size-6 items-center justify-center rounded-full bg-ember text-2xs font-semibold text-ink-inverse"
                      : reached
                        ? "flex size-6 items-center justify-center rounded-full bg-ember-subtle text-ember-strong"
                        : "flex size-6 items-center justify-center rounded-full border border-line text-2xs text-ink-muted"
                  }
                >
                  {reached && !current ? (
                    <Check className="size-3.5" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={
                    current
                      ? "text-2xs font-medium text-ink"
                      : "text-2xs text-ink-muted"
                  }
                >
                  {item.label}
                </span>
              </li>
              {index < STEPS.length - 1 && (
                <li
                  aria-hidden
                  className={
                    reached
                      ? "mx-3 h-px min-w-4 flex-1 bg-ember/40"
                      : "mx-3 h-px min-w-4 flex-1 bg-line"
                  }
                />
              )}
            </Fragment>
          );
        })}
      </ol>

      {step === "connect" && <ConnectStep />}
      {step === "import" && (
        <ImportStep
          listingCount={setup.data.listingCount}
          queue={
            setup.data.import ?? {
              waiting: 0,
              active: 0,
              workerOnline: true,
            }
          }
        />
      )}
      {step === "map" && <MapStep />}
      {step === "done" && (
        <Card>
          <EmptyState
            icon={<Check />}
            title="Tudo pronto para operar"
            description="Já existe pelo menos um anúncio vinculado a um SKU. Pedidos pagos passam a baixar o estoque central."
            action={
              <Link
                to="/home"
                className="text-2xs font-medium text-ember hover:underline"
              >
                Ir para a visão geral →
              </Link>
            }
          />
        </Card>
      )}
    </>
  );
}

function ConnectStep() {
  const toast = useToast();
  const billing = useQuery({
    queryKey: ["billing"],
    queryFn: async () => unwrap(await api.billing.get()),
  });
  const shopee = useQuery({
    queryKey: ["shopee-status"],
    queryFn: async () => unwrap(await api.shopee.status.get()),
  });
  const connect = useMutation({
    mutationFn: async (provider: "mercadolivre" | "shopee") =>
      unwrap(
        provider === "shopee"
          ? await api.shopee.oauth.start.get({ query: { returnTo: "setup" } })
          : await api.ml.oauth.start.get({ query: { returnTo: "setup" } }),
      ),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title:
          error instanceof Error
            ? error.message
            : "Não foi possível iniciar a conexão",
      });
    },
  });

  const atAccountLimit = billing.data ? !billing.data.canConnectAccount : false;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div>
          <p className="text-[0.8125rem] font-semibold text-ink">
            Conecte a primeira loja
          </p>
          <p className="mt-1 text-2xs text-ink-muted">
            Você autoriza o Bridge a ler pedidos, anúncios e perguntas desta
            conta. A escrita no marketplace continua desligada até você ativar.
          </p>
        </div>
        {atAccountLimit && (
          <p className="text-2xs text-ink-muted">
            Limite de lojas do seu plano atingido.
          </p>
        )}
        <div className={shopee.data?.configured ? "grid gap-2 sm:grid-cols-2" : "flex"}>
          <button
            type="button"
            disabled={atAccountLimit || connect.isPending}
            onClick={() => connect.mutate("mercadolivre")}
            className="flex flex-col items-start gap-1 rounded-control border border-line bg-surface px-3 py-3 text-left transition-colors duration-150 hover:border-ink-faint disabled:opacity-50"
          >
            <span className="text-[0.8125rem] font-semibold text-ink">Mercado Livre</span>
            <span className="text-2xs leading-relaxed text-ink-muted">
              Pedidos, anúncios, perguntas, mensagens e etiqueta PDF ou ZPL.
            </span>
          </button>
          {shopee.data?.configured ? (
            <button
              type="button"
              disabled={atAccountLimit || connect.isPending}
              onClick={() => connect.mutate("shopee")}
              className="flex flex-col items-start gap-1 rounded-control border border-line bg-surface px-3 py-3 text-left transition-colors duration-150 hover:border-ink-faint disabled:opacity-50"
            >
              <span className="text-[0.8125rem] font-semibold text-ink">Shopee</span>
              <span className="text-2xs leading-relaxed text-ink-muted">
                Pedidos, anúncios, chat, devoluções e etiqueta em PDF. A NF-e você envia.
              </span>
            </button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

const IMPORT_FLAG = "fc-setup-importing";

function ImportStep({
  listingCount,
  queue,
}: {
  listingCount: number;
  queue: { waiting: number; active: number; workerOnline: boolean };
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [awaiting, setAwaiting] = useState(() => {
    if (typeof sessionStorage === "undefined") return false;
    return sessionStorage.getItem(IMPORT_FLAG) === "1";
  });

  const inFlight = queue.waiting + queue.active > 0;
  const tracking = awaiting || inFlight;

  useEffect(() => {
    if (listingCount > 0) {
      sessionStorage.removeItem(IMPORT_FLAG);
      setAwaiting(false);
    }
  }, [listingCount]);

  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });

  const connectedAccounts =
    accounts.data?.accounts.filter((account) => account.status === "connected") ??
    [];

  const importAll = useMutation({
    mutationFn: async () => {
      const list = connectedAccounts;
      for (const account of list) {
        await unwrap(
          await api.ml.accounts({ id: account.id })["sync-listings"].post(),
        );
        await unwrap(
          await api.ml.accounts({ id: account.id })["sync-orders"].post(),
        );
      }
    },
    onSuccess: () => {
      sessionStorage.setItem(IMPORT_FLAG, "1");
      setAwaiting(true);
      toast.add({
        type: "success",
        title: "Importação na fila",
        description: "Acompanhe o progresso aqui. Não precisa clicar de novo.",
      });
      void queryClient.invalidateQueries({ queryKey: ["setup"] });
      void queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
      void queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
  });

  const busy = importAll.isPending || tracking;
  const status = importAll.isPending
    ? "Enfileirando a importação…"
    : queue.active > 0
      ? "O worker está importando anúncios e pedidos."
      : queue.waiting > 0 && queue.workerOnline
        ? `${queue.waiting} job${queue.waiting === 1 ? "" : "s"} na fila.`
        : queue.waiting > 0 && !queue.workerOnline
          ? "Os jobs estão na fila, mas nenhum worker está ligado. No Coolify, publique um segundo serviço com a mesma imagem, FORGE_ROLE=worker e o comando bun apps/api/src/worker.ts."
          : tracking && !queue.workerOnline
            ? "Nenhum worker está consumindo a fila. Publique o serviço worker no Coolify (mesma imagem, FORGE_ROLE=worker)."
            : tracking
              ? "Aguardando o primeiro anúncio…"
              : "A sincronização roda em segundo plano. Esta página atualiza sozinha quando o primeiro anúncio chegar.";

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div>
          <p className="text-[0.8125rem] font-semibold text-ink">
            Importe anúncios e pedidos
          </p>
          <p className="mt-1 text-2xs text-ink-muted">{status}</p>
        </div>
        {tracking && (
          <div className="flex items-center gap-2 rounded-control border border-line bg-surface-sunken px-3 py-2 text-2xs text-ink-secondary">
            <Spinner className="size-3.5 text-ember" />
            {listingCount > 0
              ? `${listingCount} anúncio${listingCount === 1 ? "" : "s"} importado${listingCount === 1 ? "" : "s"}`
              : queue.active > 0
                ? "Processando…"
                : "Na fila…"}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {connectedAccounts.map((account) => (
            <Badge key={account.id} variant="neutral">
              {account.displayName}
            </Badge>
          ))}
        </div>
        <div>
          <Button
            loading={busy}
            disabled={!connectedAccounts.length || busy}
            onClick={() => importAll.mutate()}
          >
            <Tags />
            {busy ? "Importando…" : "Importar agora"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MapStep() {
  const listings = useQuery({
    queryKey: ["listings", null],
    queryFn: async () => unwrap(await api.listings.get({ query: {} })),
  });
  const unmapped =
    listings.data?.listings.filter((listing) => listing.mappingCount === 0) ??
    [];

  return (
    <Card>
      {listings.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : unmapped.length === 0 ? (
        <EmptyState
          icon={<Package />}
          title="Nenhum anúncio sem vínculo"
          description="Todos os anúncios importados já têm SKU. Você pode criar kits depois em Estoque."
        />
      ) : (
        <div className="divide-y divide-line">
          <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[0.8125rem] font-semibold text-ink">
                Vincule os anúncios a um SKU
              </p>
              <p className="mt-1 text-2xs text-ink-muted">
                Se o anúncio já tem SKU no canal, vincule em massa.
                Kits e anúncios sem código continuam um a um.
              </p>
            </div>
            <BulkMapFromSku />
          </div>
          {unmapped.map((listing) => (
            <MapListingRow key={listing.id} listing={listing} />
          ))}
        </div>
      )}
    </Card>
  );
}

function MapListingRow({
  listing,
}: {
  listing: {
    id: string;
    title: string;
    externalItemId: string;
    accountNickname: string;
    thumbnailUrl: string | null;
    sellerSku: string | null;
  };
}) {
  const [code, setCode] = useState(listing.sellerSku ?? "");
  const [quantity, setQuantity] = useState("1");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const warehouses = useQuery({
    queryKey: ["warehouses"],
    queryFn: async () => unwrap(await api.warehouses.get()),
  });
  const queryClient = useQueryClient();
  const toast = useToast();
  const options = warehouses.data?.warehouses ?? [];
  const effectiveWarehouse = warehouseId ?? options[0]?.id ?? null;

  const map = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.listings({ id: listing.id }).map.post({
          skuCode: code.trim(),
          skuTitle: listing.title,
          warehouseId: effectiveWarehouse ?? undefined,
          quantity: Number(quantity) || 1,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      void queryClient.invalidateQueries({ queryKey: ["listings-map-preview"] });
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      void queryClient.invalidateQueries({ queryKey: ["setup"] });
      toast.add({ type: "success", title: `Vinculado a ${code.trim()}` });
      setCode("");
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível vincular",
        description:
          error instanceof Error ? error.message : "Tente outro código.",
      });
    },
  });

  return (
    <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-end">
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {secureAssetUrl(listing.thumbnailUrl) && (
          <img
            src={secureAssetUrl(listing.thumbnailUrl)!}
            alt=""
            className="size-8 shrink-0 rounded-control border border-line object-cover"
          />
        )}
        <div className="min-w-0">
          <p className="truncate text-[0.8125rem] font-medium text-ink">
            {listing.title}
          </p>
          <p className="font-mono text-2xs text-ink-muted">
            {listing.accountNickname} · {listing.externalItemId}
            {listing.sellerSku ? ` · ${listing.sellerSku}` : ""}
          </p>
        </div>
      </div>
      <Field className="lg:w-40">
        <FieldLabel>SKU</FieldLabel>
        <Input
          value={code}
          placeholder="ABC123"
          onChange={(event) => setCode(event.target.value)}
        />
      </Field>
      <Field className="lg:w-44">
        <FieldLabel>Depósito</FieldLabel>
        <Select
          value={effectiveWarehouse}
          itemToStringLabel={(id) =>
            options.find((warehouse) => warehouse.id === id)?.name ??
            "Depósito"
          }
          onValueChange={(value) => setWarehouseId(value as string | null)}
        >
          <SelectTrigger placeholder="Depósito" className="w-full" />
          <SelectContent>
            {options.map((warehouse) => (
              <SelectItem key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field className="lg:w-24">
        <FieldLabel>Qtd</FieldLabel>
        <Input
          type="number"
          min="1"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />
      </Field>
      <Button
        size="sm"
        disabled={!code.trim() || !effectiveWarehouse}
        loading={map.isPending}
        onClick={() => map.mutate()}
      >
        Vincular
      </Button>
    </div>
  );
}
